import ExcelJS from 'exceljs';

const sanitizeSheetName = (value) => (value || 'AI Report').replace(/[\\/*?:[\]]/g, ' ').slice(0, 31);

const safeText = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const formatTimestamp = () => new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);

const addKeyValueRow = (sheet, label, value) => {
  const row = sheet.addRow([label, safeText(value)]);
  row.getCell(1).font = { bold: true };
  row.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEAF1F8' } };
  row.getCell(2).alignment = { wrapText: true, vertical: 'top' };
};

const splitReportLines = (text) => safeText(text)
  .replace(/\r\n/g, '\n')
  .split('\n')
  .map((line) => line.trimEnd());

export async function exportAiReportExcel({ report, companyName, periodLabel, language = 'tr-TR' }) {
  if (!report) {
    throw new Error(language.startsWith('tr') ? 'Excel icin once rapor olusturun.' : 'Generate a report before exporting Excel.');
  }

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'RatioNexus';
  workbook.created = new Date();
  workbook.modified = new Date();

  const reportSheet = workbook.addWorksheet(sanitizeSheetName('AI Report'), {
    views: [{ state: 'frozen', ySplit: 1 }]
  });
  reportSheet.columns = [
    { header: 'Alan', key: 'label', width: 28 },
    { header: 'Deger', key: 'value', width: 110 }
  ];
  reportSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
  reportSheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F4E79' } };

  addKeyValueRow(reportSheet, language.startsWith('tr') ? 'Sirket' : 'Company', companyName);
  addKeyValueRow(reportSheet, language.startsWith('tr') ? 'Donem' : 'Period', periodLabel);
  addKeyValueRow(reportSheet, language.startsWith('tr') ? 'Kayit Adi' : 'Saved Name', report.savedReportName);
  addKeyValueRow(reportSheet, 'Model', report.modelName);
  addKeyValueRow(reportSheet, language.startsWith('tr') ? 'Kaynak' : 'Source', report.source);
  addKeyValueRow(reportSheet, language.startsWith('tr') ? 'Gecikme' : 'Latency', `${report.latencyMs ?? 0} ms`);
  reportSheet.addRow([]);

  const heading = reportSheet.addRow([language.startsWith('tr') ? 'Rapor Metni' : 'Report Text', '']);
  heading.getCell(1).font = { bold: true };
  heading.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9EAF7' } };

  splitReportLines(report.reportText).forEach((line) => {
    const row = reportSheet.addRow(['', line]);
    row.getCell(2).alignment = { wrapText: true, vertical: 'top' };
    if (line.startsWith('#')) {
      row.getCell(2).font = { bold: true };
      row.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F6FA' } };
    }
  });

  reportSheet.eachRow((row) => {
    row.eachCell((cell) => {
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
        left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
        bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
        right: { style: 'thin', color: { argb: 'FFE5E7EB' } }
      };
    });
  });

  const usageSheet = workbook.addWorksheet('Usage');
  usageSheet.columns = [
    { header: 'Metric', key: 'metric', width: 28 },
    { header: 'Value', key: 'value', width: 22 }
  ];
  usageSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
  usageSheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F4E79' } };
  const usage = report.usage || {};
  [
    ['Prompt Tokens', usage.promptTokens],
    ['Completion Tokens', usage.completionTokens],
    ['Total Tokens', usage.totalTokens],
    ['Input Cost USD', usage.inputCostUsd],
    ['Output Cost USD', usage.outputCostUsd],
    ['Total Cost USD', usage.totalCostUsd],
    ['Currency', usage.currency || 'USD']
  ].forEach(([metric, value]) => usageSheet.addRow([metric, value ?? 0]));

  const buffer = await workbook.xlsx.writeBuffer();
  const filename = `ai-report-${formatTimestamp()}.xlsx`;
  downloadBlob(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), filename);
}
